// Export utilities for message content (Markdown, CSV, XLSX, PDF).
// All heavy dependencies (xlsx, html2pdf.js) are lazy-loaded on use.

import { parseMarkdownToExcelRows, parseMarkdownTables } from './markdownParse';
import { normalizeAssistantMarkdown } from './normalizeAssistantMarkdown';

export { hasTableData } from './markdownParse';

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ts(): string {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

function agentLabel(agentId: string): string {
  const map: Record<string, string> = {
    'esg-executive-summary-claude': 'Account Deep Dive',
    'retention-churn-mitigation': 'Retention',
    'test_retention_churn_mitigation': 'Test Retention Demo',
  };
  return (map[agentId] || agentId.replace(/-/g, ' '))
    .replace(/[<>:"/\\|?*]+/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

export function buildFilename(ext: string, agentId?: string): string {
  const parts: string[] = [];
  if (agentId) parts.push(agentLabel(agentId));
  parts.push(ts());
  return `${parts.join('-')}.${ext}`;
}

export function downloadAsMd(content: string, filename: string): void {
  // Same delimiter and split-decimal repair the chat surface applies, so the
  // exported file renders its tables and figures in other Markdown viewers too.
  downloadBlob(new Blob([normalizeAssistantMarkdown(content)], { type: 'text/markdown;charset=utf-8' }), filename);
}

export function downloadAsCsv(content: string, filename: string): void {
  const rows = parseMarkdownToExcelRows(content);
  if (rows.length === 0) rows.push(['(empty response)']);
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
}

export async function downloadAsExcel(content: string, filename: string): Promise<void> {
  const XLSX = await import('xlsx');
  const data = parseMarkdownToExcelRows(content);
  if (data.length === 0) data.push(['(empty response)']);
  const ws = XLSX.utils.aoa_to_sheet(data);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  if (!ws['!cols']) ws['!cols'] = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    let maxLen = 10;
    for (let r = range.s.r; r <= Math.min(range.e.r, 50); r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell?.v) maxLen = Math.max(maxLen, String(cell.v).length);
    }
    ws['!cols'][c] = { wch: Math.min(maxLen + 2, 60) };
  }
  if (parseMarkdownTables(content).length > 1) {
    ws['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  downloadBlob(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename,
  );
}

export async function downloadAsPdf(content: string, filename: string): Promise<void> {
  const DOMPurify = (await import('dompurify')).default;
  const { marked } = await import('marked');
  const html2pdf = (await import('html2pdf.js')).default;

  const rawHtml = await marked.parse(content);
  const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ['h1','h2','h3','h4','h5','h6','p','ul','ol','li','table','thead','tbody','tr',
      'th','td','code','pre','blockquote','em','strong','a','br','hr','img','span','div','sup',
      'sub','dl','dt','dd','s','del'],
    ALLOWED_ATTR: ['href','src','alt','class','style','target','rel'],
    ALLOW_DATA_ATTR: false,
  });

  // Font stack tracks the live theme's --font-sans (single source of truth,
  // avoids a third hand-typed copy drifting from tailwind.css/base.css).
  // Colors stay fixed/print-friendly on purpose: a PDF export must stay
  // legible on paper regardless of whether the app is currently in dark mode.
  const fontSans = getComputedStyle(document.documentElement).getPropertyValue('--font-sans').trim()
    || 'adobe-clean, "Source Sans 3", "Source Sans Pro", ui-sans-serif, system-ui, sans-serif';
  const container = document.createElement('div');
  container.innerHTML = sanitizedHtml;
  container.style.cssText =
    `font-family:${fontSans};font-size:14px;line-height:1.6;color:#222;background:#ffffff;max-width:800px;padding:20px;`;
  const styleEl = document.createElement('style');
  styleEl.textContent = 'h1{font-size:1.4em;page-break-after:avoid}h2{font-size:1.2em;page-break-after:avoid}h3{font-size:1.05em;page-break-after:avoid}p{page-break-inside:avoid}table{border-collapse:collapse;width:100%;margin:12px 0}th,td{border:1px solid #ccc;padding:6px 10px;font-size:12px}th{background:#f5f5f5;font-weight:600}code{background:#f0f0f0;padding:1px 4px;border-radius:3px;font-size:12px}pre{background:#f5f5f5;padding:12px;border-radius:6px;page-break-inside:avoid}';
  container.prepend(styleEl);
  document.body.appendChild(container);
  try {
    await html2pdf()
      .set({
        margin: [15, 12, 15, 12], filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css'] },
      } as Record<string, unknown>)
      .from(container).save();
  } finally {
    document.body.removeChild(container);
  }
}
